import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Dispatch, SetStateAction } from "react";
import {
  BentoSearchFilter,
  OrganizationProfile,
  ProfileType,
} from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import QuestionChip from "components/Styled/QuestionChip";
import StyledTextField from "components/Styled/TextField";
import { titleCase } from "utils/string";

const styles = {
  questionBox: {
    border: `1px solid ${grey[300]}`,
    borderRadius: "16px",
    p: 2,
    width: "100%",
  },
};

interface IProps {
  vaSearchFilters: BentoSearchFilter[];
  profile: OrganizationProfile | null;
  setProfile: Dispatch<SetStateAction<OrganizationProfile | null>>;
  includedBentoSearchFilterIds: number[];
  excludedBentoSearchFilterIds: number[];
  setIncludedBentoSearchFilterIds: Dispatch<SetStateAction<number[]>>;
  setExcludedBentoSearchFilterIds: Dispatch<SetStateAction<number[]>>;
  setIsEdited: Dispatch<SetStateAction<boolean>>;
}

export default function OnboardingSettings({
  vaSearchFilters,
  profile,
  setProfile,
  includedBentoSearchFilterIds,
  excludedBentoSearchFilterIds,
  setIncludedBentoSearchFilterIds,
  setExcludedBentoSearchFilterIds,
  setIsEdited,
}: IProps) {
  const includeSearchFilters = vaSearchFilters.filter(
    (f) => f.enableForVaInclude,
  );

  const excludeSearchFilters = vaSearchFilters.filter(
    (f) => f.enableForVaExclude,
  );

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography gutterBottom>Who are you?</Typography>
        <Autocomplete
          multiple
          options={Object.values(ProfileType)}
          getOptionLabel={(option) => option}
          value={profile?.profileTypes || []}
          onChange={(_, newValue) => {
            setIsEdited(true);
            setProfile((p) => {
              if (!p) return p;
              const copy = { ...p };
              copy.profileTypes = newValue;
              return copy;
            });
          }}
          sx={{
            "& fieldset.MuiOutlinedInput-notchedOutline": {
              borderRadius: 4,
              borderColor: grey[300],
            },
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                {...getTagProps({ index })}
                key={option}
                color="primary"
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} placeholder="Select all that apply" />
          )}
        />
        {profile?.profileTypes?.includes(ProfileType.other) && (
          <TextField
            placeholder="Please specify"
            value={profile?.otherType || ""}
            onChange={(e: CustomEvent) => {
              const { value } = e.target;
              setIsEdited(true);
              setProfile((p) => {
                if (!p) return p;
                const copy = { ...p };
                copy.otherType = value;
                return copy;
              });
            }}
            variant="standard"
            sx={{ mt: 1 }}
          />
        )}
      </Box>
      <Box sx={{ mb: 2 }} display="flex" flexDirection="column">
        <Typography gutterBottom>Who do you want to pitch to?</Typography>
        <StyledTextField
          value={profile?.targetBrandTypes || ""}
          onChange={(e: CustomEvent) => {
            const { value } = e.target;
            setIsEdited(true);
            setProfile((p) => {
              if (!p) return p;
              const copy = { ...p };
              copy.targetBrandTypes = value;
              return copy;
            });
          }}
          placeholder="Restaurant managers, PR team, Event sponsors, Influencers, Marketing managers..."
          variant="outlined"
          fullWidth
          multiline
          rows={2}
        />
      </Box>
      <Box sx={styles.questionBox} display="flex" flexDirection="column">
        <Box>
          <Typography gutterBottom>
            Tell us about you and the content you create
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary" paragraph>
            Select all that apply — this helps us tailor opportunities that fit
            you best.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {includeSearchFilters.map((searchFilter: BentoSearchFilter) => (
            <QuestionChip
              key={searchFilter.id}
              isSelected={
                includedBentoSearchFilterIds.indexOf(searchFilter.id) > -1
              }
              label={searchFilter.vaDisplayName || titleCase(searchFilter.name)}
              onClick={() => {
                setIsEdited(true);
                setIncludedBentoSearchFilterIds((prev) => {
                  if (prev.includes(searchFilter.id)) {
                    return prev.filter((item) => item !== searchFilter.id);
                  } else {
                    return [...prev, searchFilter.id];
                  }
                });
              }}
            />
          ))}
        </Box>
      </Box>
      <Box sx={styles.questionBox} display="flex" flexDirection="column">
        <Box>
          <Typography gutterBottom>
            Are there any categories or industries you'd like us to avoid
            pitching you to?
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary" paragraph>
            Select all that apply — we'll make every effort to exclude
            opportunities in these areas.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {excludeSearchFilters.map((searchFilter: BentoSearchFilter) => (
            <QuestionChip
              key={searchFilter.id}
              color="secondary"
              isSelected={
                excludedBentoSearchFilterIds.indexOf(searchFilter.id) > -1
              }
              label={searchFilter.vaDisplayName || titleCase(searchFilter.name)}
              onClick={() => {
                setIsEdited(true);
                setExcludedBentoSearchFilterIds((prev) => {
                  if (prev.includes(searchFilter.id)) {
                    return prev.filter((item) => item !== searchFilter.id);
                  } else {
                    return [...prev, searchFilter.id];
                  }
                });
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}
